import { useState, useEffect, useCallback, useRef } from 'react'
import { PageMetadata } from '@/types/page'
import { useHighlight } from '@/hooks/useHighlight'
import { StylingPresetToggle } from './shared/StylingPresetToggle'
import { CodeOutput } from './shared/CodeOutput'
import { generateCode } from './shared/widgetState'

export const metadata: PageMetadata = {
  id: 'customization-embedded',
  category: 'customization',
  categoryName: 'Customization',
  name: 'Customization',
  icon: '✨',
  path: '/customization/embedded',
  title: 'Zendesk Widget - Embedded Customization',
  description: 'Comprehensive customization demo with embedded conversation list and message log'
}

// State type matching the HTML version
type WidgetState = {
  stylingPreset: 'default' | 'minimalistic'
  settings: {
    hideHeader: boolean
    hideNewConversationButton: boolean
    conversationList: boolean
    messageLog: boolean
  }
  conversationListSettings: {
    avatar: { hidden: boolean; size: number }
    hideTimestamp: boolean
    hideMessagePreview: boolean
    hideDivider: boolean
    groupByTimestamp: boolean
  }
  avatar: {
    hidden: boolean
    position: 'side' | 'top' | 'top-side'
    size: number
  }
  messageBubbles: {
    business: { bubbleMaxWidth?: number; hideBubble: boolean }
    user: { bubbleMaxWidth?: number; hideBubble: boolean }
  }
}

export default function Minimalistic() {
  useEffect(() => {
    document.title = `Internal Note - ${metadata.title}`
  }, [])

  // Just show the working HTML test page in an iframe - make it seamless
  return (
    <div className="fixed inset-0 md:left-72 top-0 bottom-0">
      <iframe
        src="/customization-test.html"
        className="w-full h-full border-0 block"
        title="Zendesk Widget Customization"
      />
    </div>
  )
}

function MinimalisticOld() {
  const [viewMode, setViewMode] = useState<'configure' | 'code'>('configure')
  const [copied, setCopied] = useState(false)

  // Use ref instead of state - needs to mutate immediately like HTML version
  const touchedSettings = useRef<Set<string>>(new Set(['hideGlobalHeader', 'preset']))

  // React state for UI
  const [state, setState] = useState<WidgetState>({
    stylingPreset: 'default',
    settings: {
      hideHeader: false,
      hideNewConversationButton: false,
      conversationList: false,
      messageLog: false
    },
    conversationListSettings: {
      avatar: { hidden: false, size: 48 },
      hideTimestamp: false,
      hideMessagePreview: false,
      hideDivider: false,
      groupByTimestamp: false
    },
    avatar: {
      hidden: false,
      position: 'side',
      size: 32
    },
    messageBubbles: {
      business: { hideBubble: false },
      user: { hideBubble: false }
    }
  })

  const codeRef = useHighlight([
    state.stylingPreset,
    state.avatar,
    state.messageBubbles,
    state.conversationListSettings,
    state.settings,
    viewMode
  ])

  // Script loading - matches working HTML test page
  useEffect(() => {
    document.title = `Internal Note - ${metadata.title}`

    // Prevent double-initialization
    if (document.getElementById('ze-snippet')) return

    // Load Zendesk script
    const script = document.createElement('script')
    script.id = 'ze-snippet'
    script.src = 'https://static.zdassets.com/ekr/snippet.js?key=e125418a-9466-44d8-9b3f-2ac10e911ea4'
    document.body.appendChild(script)

    // Poll for zE and initialize
    const interval = setInterval(() => {
      if (window.zE) {
        clearInterval(interval)

        window.zEMessenger = { autorender: false }

        window.zE('messenger', 'render', {
          mode: 'embedded',
          conversationList: { targetElement: '#widget_conversations' },
          messageLog: { targetElement: '#widget_messages' }
        })
      }
    }, 50)

    return () => {
      clearInterval(interval)
      script?.remove()
    }
  }, [])

  // Apply customization - matches working HTML test page logic
  const applyCustomization = useCallback(() => {
    if (!window.zE) return

    const config: any = {}
    const hideGlobalHeader = state.settings.hideHeader

    // Common settings
    if (touchedSettings.current.has('hideGlobalHeader') || touchedSettings.current.has('preset')) {
      config.common = {}
      if (touchedSettings.current.has('hideGlobalHeader')) {
        config.common.hideHeader = hideGlobalHeader
      }
      if (touchedSettings.current.has('preset')) {
        config.common.stylingPreset = state.stylingPreset
      }
    }

    // Conversation List
    const convListTouched = ['hideNewConvButton', 'hideConvListHeader', 'hideConvListAvatar',
                              'convListAvatarSize', 'hideTimestamp', 'hideMessagePreview',
                              'hideDivider', 'groupByTimestamp']
    const hasConvListSettings = convListTouched.some(s => touchedSettings.current.has(s))

    if (hasConvListSettings) {
      config.conversationList = {}

      if (touchedSettings.current.has('hideConvListHeader')) {
        if (!hideGlobalHeader) {
          config.conversationList.hideHeader = state.settings.conversationList
        }
      }

      if (touchedSettings.current.has('hideNewConvButton')) {
        config.conversationList.hideNewConversationButton = state.settings.hideNewConversationButton
      }
      if (touchedSettings.current.has('hideConvListAvatar') || touchedSettings.current.has('convListAvatarSize')) {
        config.conversationList.avatar = {
          hidden: state.conversationListSettings.avatar.hidden,
          size: state.conversationListSettings.avatar.size
        }
      }
      if (touchedSettings.current.has('hideTimestamp')) {
        config.conversationList.hideTimestamp = state.conversationListSettings.hideTimestamp
      }
      if (touchedSettings.current.has('hideMessagePreview')) {
        config.conversationList.hideMessagePreview = state.conversationListSettings.hideMessagePreview
      }
      if (touchedSettings.current.has('hideDivider')) {
        config.conversationList.hideDivider = state.conversationListSettings.hideDivider
      }
      if (touchedSettings.current.has('groupByTimestamp')) {
        config.conversationList.groupByTimestamp = state.conversationListSettings.groupByTimestamp
      }

      if (Object.keys(config.conversationList).length === 0) {
        delete config.conversationList
      }
    }

    // Message Log
    const msgLogTouched = ['hideMessageLogHeader', 'hideAvatar', 'avatarSize', 'avatarPosition',
                           'hideBusinessBubble', 'businessBubbleWidth', 'hideUserBubble', 'userBubbleWidth']
    const hasMsgLogSettings = msgLogTouched.some(s => touchedSettings.current.has(s))

    if (hasMsgLogSettings) {
      config.messageLog = {}

      if (touchedSettings.current.has('hideMessageLogHeader')) {
        if (!hideGlobalHeader) {
          config.messageLog.hideHeader = state.settings.messageLog
        }
      }

      if (touchedSettings.current.has('hideAvatar') || touchedSettings.current.has('avatarSize') || touchedSettings.current.has('avatarPosition')) {
        config.messageLog.avatar = {
          hidden: state.avatar.hidden,
          size: state.avatar.size,
          position: state.avatar.position
        }
      }

      if (touchedSettings.current.has('hideBusinessBubble') || touchedSettings.current.has('businessBubbleWidth')) {
        config.messageLog.businessMessage = {
          hideBubble: state.messageBubbles.business.hideBubble
        }
        if (state.messageBubbles.business.bubbleMaxWidth) {
          config.messageLog.businessMessage.bubbleMaxWidth = state.messageBubbles.business.bubbleMaxWidth
        }
      }

      if (touchedSettings.current.has('hideUserBubble') || touchedSettings.current.has('userBubbleWidth')) {
        config.messageLog.userMessage = {
          hideBubble: state.messageBubbles.user.hideBubble
        }
        if (state.messageBubbles.user.bubbleMaxWidth) {
          config.messageLog.userMessage.bubbleMaxWidth = state.messageBubbles.user.bubbleMaxWidth
        }
      }

      if (Object.keys(config.messageLog).length === 0) {
        delete config.messageLog
      }
    }

    console.log('Applying customization:', config)
    window.zE("messenger:set", "customization", config)
  }, [state, touchedSettings])

  const handleCopy = () => {
    const code = generateCode({
      stylingPreset: state.stylingPreset,
      avatar: state.avatar,
      messageBubbles: state.messageBubbles,
      conversationListSettings: state.conversationListSettings,
      settings: state.settings
    })
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const markTouched = (settingId: string) => {
    touchedSettings.current.add(settingId)
  }

  const updateSettings = (updates: Partial<typeof state.settings>) => {
    setState(prev => ({
      ...prev,
      settings: { ...prev.settings, ...updates }
    }))
    Object.keys(updates).forEach(key => markTouched(key))
    setTimeout(() => applyCustomization(), 0)
  }

  const updateStylingPreset = (preset: 'default' | 'minimalistic') => {
    setState(prev => ({ ...prev, stylingPreset: preset }))
    markTouched('preset')
    setTimeout(() => applyCustomization(), 0)
  }

  const updateAvatar = (updates: Partial<typeof state.avatar>) => {
    setState(prev => ({
      ...prev,
      avatar: { ...prev.avatar, ...updates }
    }))
    Object.keys(updates).forEach(key => markTouched(`avatar${key.charAt(0).toUpperCase()}${key.slice(1)}`))
    setTimeout(() => applyCustomization(), 0)
  }

  const updateMessageBubbles = (
    type: 'business' | 'user',
    field: 'bubbleMaxWidth' | 'hideBubble',
    value: number | boolean | undefined
  ) => {
    setState(prev => ({
      ...prev,
      messageBubbles: {
        ...prev.messageBubbles,
        [type]: {
          ...prev.messageBubbles[type],
          [field]: value
        }
      }
    }))
    markTouched(`${type === 'business' ? 'business' : 'user'}Bubble${field === 'bubbleMaxWidth' ? 'Width' : ''}`)
    setTimeout(() => applyCustomization(), 0)
  }

  const updateConversationList = (updates: Partial<typeof state.conversationListSettings>) => {
    setState(prev => ({
      ...prev,
      conversationListSettings: { ...prev.conversationListSettings, ...updates }
    }))
    Object.keys(updates).forEach(key => markTouched(key))
    setTimeout(() => applyCustomization(), 0)
  }

  const updateConversationListAvatar = (updates: Partial<typeof state.conversationListSettings.avatar>) => {
    setState(prev => ({
      ...prev,
      conversationListSettings: {
        ...prev.conversationListSettings,
        avatar: { ...prev.conversationListSettings.avatar, ...updates }
      }
    }))
    markTouched('convListAvatarSize')
    setTimeout(() => applyCustomization(), 0)
  }

  return (
    <>
      <header>
        <div className="py-6 px-4 sm:px-6 md:px-8 mt-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl text-licorice dark:text-white">{metadata.title}</h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {metadata.description}
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="py-6 px-4 sm:px-6 lg:px-8 mb-32">
        {/* Top Controls - Full Width */}
        <div className="flex justify-between items-center mb-8">
          {/* View Mode Toggle - Left */}
          <nav className="isolate flex divide-x divide-gray-200 rounded-lg bg-white dark:bg-licorice border border-gray-200">
            <button
              onClick={() => setViewMode('configure')}
              className={`px-6 py-2 text-sm font-medium rounded-l-lg ${
                viewMode === 'configure'
                  ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:hover:bg-gray-800'
              }`}
            >
              Configure
            </button>
            <button
              onClick={() => setViewMode('code')}
              className={`px-6 py-2 text-sm font-medium rounded-r-lg ${
                viewMode === 'code'
                  ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:hover:bg-gray-800'
              }`}
            >
              Code
            </button>
          </nav>

          {/* Styling Preset Toggle - Right */}
          {viewMode === 'configure' && (
            <StylingPresetToggle
              value={state.stylingPreset}
              onChange={updateStylingPreset}
            />
          )}
        </div>

        {viewMode === 'configure' ? (
          <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr_1fr] gap-4">
            {/* Column 1: All Settings - Scrollable */}
            <div className="space-y-4 max-h-[calc(100vh-220px)] overflow-y-auto pr-2">
              {/* Global Settings */}
              <section className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-semibold mb-3 text-licorice dark:text-white">Global Settings</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input id="hideGlobalHeader" type="checkbox" checked={state.settings.hideHeader} onChange={(e) => updateSettings({ hideHeader: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300" />
                    <label htmlFor="hideGlobalHeader" className="text-xs text-licorice dark:text-white">Hide global header</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input id="hideNewConvButton" type="checkbox" checked={state.settings.hideNewConversationButton} onChange={(e) => updateSettings({ hideNewConversationButton: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300" />
                    <label htmlFor="hideNewConvButton" className="text-xs text-licorice dark:text-white">Hide new conversation button</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input id="hideConvListHeader" type="checkbox" checked={state.settings.conversationList} onChange={(e) => updateSettings({ conversationList: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300" />
                    <label htmlFor="hideConvListHeader" className="text-xs text-licorice dark:text-white">Hide conversation list header</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input id="hideMessageLogHeader" type="checkbox" checked={state.settings.messageLog} onChange={(e) => updateSettings({ messageLog: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300" />
                    <label htmlFor="hideMessageLogHeader" className="text-xs text-licorice dark:text-white">Hide message log header</label>
                  </div>
                </div>
              </section>

              {/* Conversation List Settings */}
              <section className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-semibold mb-3 text-licorice dark:text-white">Conversation List</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input id="hideConvListAvatar" type="checkbox" checked={state.conversationListSettings.avatar.hidden} onChange={(e) => updateConversationListAvatar({ hidden: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300" />
                    <label htmlFor="hideConvListAvatar" className="text-xs text-licorice dark:text-white">Hide avatar</label>
                  </div>
                  <div>
                    <label className="text-xs text-licorice dark:text-white">Avatar Size (px)</label>
                    <input type="number" min="16" max="64" value={state.conversationListSettings.avatar.size} onChange={(e) => updateConversationListAvatar({ size: Number(e.target.value) })} className="mt-1 w-full px-2 py-1 border rounded text-xs text-gray-600" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input id="hideTimestamp" type="checkbox" checked={state.conversationListSettings.hideTimestamp} onChange={(e) => updateConversationList({ hideTimestamp: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300" />
                    <label htmlFor="hideTimestamp" className="text-xs text-licorice dark:text-white">Hide timestamps</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input id="hideMessagePreview" type="checkbox" checked={state.conversationListSettings.hideMessagePreview} onChange={(e) => updateConversationList({ hideMessagePreview: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300" />
                    <label htmlFor="hideMessagePreview" className="text-xs text-licorice dark:text-white">Hide message previews</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input id="hideDivider" type="checkbox" checked={state.conversationListSettings.hideDivider} onChange={(e) => updateConversationList({ hideDivider: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300" />
                    <label htmlFor="hideDivider" className="text-xs text-licorice dark:text-white">Hide divider lines</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input id="groupByTimestamp" type="checkbox" checked={state.conversationListSettings.groupByTimestamp} onChange={(e) => updateConversationList({ groupByTimestamp: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300" />
                    <label htmlFor="groupByTimestamp" className="text-xs text-licorice dark:text-white">Group by timeframe</label>
                  </div>
                </div>
              </section>

              {/* Message Log Settings */}
              <section className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-semibold mb-3 text-licorice dark:text-white">Message Log</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input id="hideAvatar" type="checkbox" checked={state.avatar.hidden} onChange={(e) => updateAvatar({ hidden: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300" />
                    <label htmlFor="hideAvatar" className="text-xs text-licorice dark:text-white">Hide avatar</label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-licorice dark:text-white">Size (px)</label>
                      <input type="number" min="16" max="64" value={state.avatar.size} onChange={(e) => updateAvatar({ size: Number(e.target.value) })} className="mt-1 w-full px-2 py-1 border rounded text-xs text-gray-600" />
                    </div>
                    <div>
                      <label className="text-xs text-licorice dark:text-white">Position</label>
                      <select value={state.avatar.position} onChange={(e) => updateAvatar({ position: e.target.value as 'side' | 'top' | 'top-side' })} className="mt-1 w-full px-2 py-1 border rounded text-xs text-gray-600">
                        <option value="side">Side</option>
                        <option value="top">Top</option>
                        <option value="top-side">Top Side</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold mb-2 text-licorice dark:text-white">Business Messages</h4>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <label className="text-xs text-licorice dark:text-white">Bubble Width (%)</label>
                        <input type="number" min="10" max="100" value={state.messageBubbles.business.bubbleMaxWidth ?? ''} onChange={(e) => updateMessageBubbles('business', 'bubbleMaxWidth', e.target.value ? Number(e.target.value) : undefined)} placeholder="Default" className="mt-1 w-full px-2 py-1 border rounded text-xs text-gray-600" />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input id="hideBusinessBubble" type="checkbox" checked={state.messageBubbles.business.hideBubble} onChange={(e) => updateMessageBubbles('business', 'hideBubble', e.target.checked)} className="h-3.5 w-3.5 rounded border-gray-300" />
                      <label htmlFor="hideBusinessBubble" className="text-xs text-licorice dark:text-white">Hide bubble background</label>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold mb-2 text-licorice dark:text-white">User Messages</h4>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <label className="text-xs text-licorice dark:text-white">Bubble Width (%)</label>
                        <input type="number" min="10" max="100" value={state.messageBubbles.user.bubbleMaxWidth ?? ''} onChange={(e) => updateMessageBubbles('user', 'bubbleMaxWidth', e.target.value ? Number(e.target.value) : undefined)} placeholder="Default" className="mt-1 w-full px-2 py-1 border rounded text-xs text-gray-600" />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input id="hideUserBubble" type="checkbox" checked={state.messageBubbles.user.hideBubble} onChange={(e) => updateMessageBubbles('user', 'hideBubble', e.target.checked)} className="h-3.5 w-3.5 rounded border-gray-300" />
                      <label htmlFor="hideUserBubble" className="text-xs text-licorice dark:text-white">Hide bubble background</label>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* Column 2: Conversation List Widget */}
            <div className="xl:sticky xl:top-24 h-fit">
              <h3 className="text-xs font-semibold mb-2 text-gray-500 dark:text-gray-400 uppercase tracking-wide">Conversation List</h3>
              <div id="widget_conversations" className="w-full h-[700px] border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900" />
            </div>

            {/* Column 3: Message Log Widget */}
            <div className="xl:sticky xl:top-24 h-fit">
              <h3 className="text-xs font-semibold mb-2 text-gray-500 dark:text-gray-400 uppercase tracking-wide">Message Log</h3>
              <div id="widget_messages" className="w-full h-[700px] border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900" />
            </div>
          </div>
        ) : (
          <CodeOutput
            code={generateCode({
              stylingPreset: state.stylingPreset,
              avatar: state.avatar,
              messageBubbles: state.messageBubbles,
              conversationListSettings: state.conversationListSettings,
              settings: state.settings
            })}
            codeRef={codeRef}
            onCopy={handleCopy}
          />
        )}
      </div>
    </>
  )
}

declare global {
  interface Window {
    zE?: any
    zEMessenger?: any
  }
}
